'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { FAKE_EARNINGS_BUCKETS_30D, USE_FAKE_REFERRAL_DATA } from '@/components/portfolio/referrals/_fake-data';
import { chartRangeToApiParams } from '@/components/portfolio/referrals/earnings-sparkline.utils';
import { type EarningsLivelinePoint, type EarningsLivelineSeries, EarningsSparklineLiveline } from '@/components/portfolio/referrals/earnings-sparkline-liveline';
import { useGetReferralEarningsChart } from '@/components/portfolio/referrals/stub-hooks';
import { D6_SCALE } from '@/config/solana';
import { useFormattedCurrency } from '@/lib/currency';
export type SparklineRange = '7d' | '30d' | '90d' | 'all';
interface EarningsBucket {
  bucket: string;
  l1Earnings: string;
  l2Earnings: string;
  totalEarnings: string;
}

/**
 * Forward-pass monotonic clamp — cumulative series may briefly regress under
 * indexer reorg; clamp to the prior value rather than render a downward step.
 * Spec 092 EC-3.
 */
function clampMonotonic(data: number[]): number[] {
  // Single-pass clamp. Earlier impl used `reduce` with `[...acc, clamped]`
  // for "immutability", but that's O(n²) and biome's noAccumulatingSpread
  // rule blocks it. Allocating a new array up-front and assigning by index
  // is O(n) and still doesn't mutate the input.
  const out = new Array<number>(data.length);
  let prev = 0;
  for (let i = 0; i < data.length; i++) {
    const v = data[i] ?? 0;
    const clamped = v >= prev ? v : prev;
    out[i] = clamped;
    prev = clamped;
  }
  return out;
}
const RANGE_DURATION_MS: Record<SparklineRange, number> = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
  all: 180 * 24 * 60 * 60 * 1000
};
const RANGE_INTERVAL_MS: Record<SparklineRange, number> = {
  '7d': 24 * 60 * 60 * 1000,
  '30d': 24 * 60 * 60 * 1000,
  '90d': 7 * 24 * 60 * 60 * 1000,
  all: 30 * 24 * 60 * 60 * 1000
};
function backfillBuckets(buckets: ReadonlyArray<EarningsBucket>, range: SparklineRange): EarningsBucket[] {
  const now = Date.now();
  const rangeStart = now - RANGE_DURATION_MS[range];
  const interval = RANGE_INTERVAL_MS[range];
  const zeroBucket = (ts: number): EarningsBucket => ({
    bucket: new Date(ts).toISOString(),
    l1Earnings: '0',
    l2Earnings: '0',
    totalEarnings: '0'
  });
  if (buckets.length === 0) {
    const count = Math.max(1, Math.floor((now - rangeStart) / interval) + 1);
    const filled = Array.from({
      length: count
    }, (_, i) => zeroBucket(rangeStart + i * interval));
    return filled.length > 0 ? filled : [zeroBucket(rangeStart), zeroBucket(now)];
  }
  const firstBucket = buckets[0];
  if (firstBucket === undefined) return [zeroBucket(rangeStart), zeroBucket(now)];
  const firstTimestamp = new Date(firstBucket.bucket).getTime();
  if (firstTimestamp <= rangeStart + interval) return [...buckets];
  const prefixCount = Math.floor((firstTimestamp - interval - rangeStart) / interval) + 1;
  const prefix = Array.from({
    length: prefixCount
  }, (_, i) => zeroBucket(rangeStart + i * interval));
  return [...prefix, ...buckets];
}
function d6ToNumber(value: string): number {
  const parsed: number = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return parsed / D6_SCALE;
}
function formatTooltipDate(unixSeconds: number, locale: string): string {
  const date = new Date(unixSeconds * 1000);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
interface EarningsSparklineProps {
  userId: string;
  range: SparklineRange;
}
export function EarningsSparkline(t0) {
  const $ = _c(25);
  const {
    userId,
    range
  } = t0;
  const locale = useLocale();
  const t = useTranslations("profile.next.summary.referrals");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== range) {
    t1 = chartRangeToApiParams(range);
    $[0] = range;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const apiParams = t1;
  let t2;
  if ($[2] !== apiParams) {
    t2 = {
      params: {
        ...apiParams,
        cumulative: "true"
      }
    };
    $[2] = apiParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = userId.length > 0;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      enabled: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data,
    status
  } = useGetReferralEarningsChart(userId, t2, t4);
  const [previous, setPrevious] = useState(null);
  let t5;
  let t6;
  if ($[6] !== data || $[7] !== range || $[8] !== status) {
    t5 = () => {
      if (status === "success" && Array.isArray(data)) {
        setPrevious({
          range,
          rawData: data as BucketSnapshot
        });
      }
    };
    t6 = [status, range, data];
    $[6] = data;
    $[7] = range;
    $[8] = status;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const renderRange = status === "success" ? range : previous?.range ?? range;
  let t7;
  if ($[11] !== data || $[12] !== previous?.rawData || $[13] !== renderRange || $[14] !== status || $[15] !== t) {
    const renderSource = status === "success" && Array.isArray(data) ? data as BucketSnapshot : previous?.rawData ?? [];
    const sdkBuckets = renderSource.map(_temp);
    const renderRaw = USE_FAKE_REFERRAL_DATA ? FAKE_EARNINGS_BUCKETS_30D.map(_temp2) : sdkBuckets;
    const filled = backfillBuckets(renderRaw, renderRange);
    const times = filled.map(_temp3);
    const l1Data = clampMonotonic(filled.map(_temp4));
    const l2DataUnstacked = clampMonotonic(filled.map(_temp5));
    const totalData = clampMonotonic(filled.map(_temp6));
    const hasAnyL2 = l2DataUnstacked.some(_temp7);
    const lastTotal = totalData[totalData.length - 1] ?? 0;
    const lastL1 = l1Data[l1Data.length - 1] ?? 0;
    const lastL2 = l2DataUnstacked[l2DataUnstacked.length - 1] ?? 0;
    const toPoints = function toPoints(values) {
      return values.map((value, i) => ({
        time: times[i] ?? 0,
        value
      }));
    };
    t7 = hasAnyL2 ? [{
      id: "direct",
      label: t("chart_legend_direct"),
      color: "var(--outcome-green-400)",
      points: toPoints(l1Data),
      value: lastL1
    }, {
      id: "indirect",
      label: t("chart_legend_indirect"),
      color: "var(--outcome-blue-400)",
      points: toPoints(l2DataUnstacked),
      value: lastL2
    }] : [{
      id: "direct",
      label: t("chart_legend_direct"),
      color: "var(--outcome-green-400)",
      points: toPoints(totalData),
      value: lastTotal
    }];
    $[11] = data;
    $[12] = previous?.rawData;
    $[13] = renderRange;
    $[14] = status;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const earningsSeries = t7;
  let t8;
  if ($[17] !== formatD6) {
    t8 = function formatValue(value_0) {
      const d6String = String(Math.round(value_0 * D6_SCALE));
      return formatD6(d6String);
    };
    $[17] = formatD6;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const formatValue = t8;
  let t9;
  if ($[19] !== locale) {
    t9 = unixSeconds => formatTooltipDate(unixSeconds, locale);
    $[19] = locale;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== earningsSeries || $[22] !== formatValue || $[23] !== t9) {
    t10 = <EarningsSparklineLiveline series={earningsSeries} stacked={true} formatValue={formatValue} formatTooltipDate={t9} />;
    $[21] = earningsSeries;
    $[22] = formatValue;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function _temp7(v) {
  return v > 0;
}
function _temp6(b_4) {
  return d6ToNumber(b_4.totalEarnings);
}
function _temp5(b_3) {
  return d6ToNumber(b_3.l2Earnings);
}
function _temp4(b_2) {
  return d6ToNumber(b_2.l1Earnings);
}
function _temp3(b_1) {
  return Math.floor(new Date(b_1.bucket).getTime() / 1000);
}
function _temp2(b_0) {
  return {
    bucket: b_0.bucket,
    l1Earnings: b_0.l1Earnings,
    l2Earnings: b_0.l2Earnings,
    totalEarnings: b_0.totalEarnings
  };
}
function _temp(b) {
  return {
    bucket: String(b.bucket ?? ""),
    l1Earnings: String(b.l1Earnings ?? "0"),
    l2Earnings: String(b.l2Earnings ?? "0"),
    totalEarnings: String(b.totalEarnings ?? "0")
  };
}